/**
 * useNotifications — TopBar bell data source.
 *
 * Two implementations, selected by the `notificationsBackend` flag:
 *
 *   ON  (default) → useBackendNotifications: reads the real
 *        /api/v1/notifications endpoints (migration 0130). Persisted rows,
 *        server-side read-state, cursor list.
 *
 *   OFF (fallback) → useLegacyNotifications: the original client-side
 *        synthesis — polls /me/leaves + /escalations every 60s and tracks
 *        read-state in localStorage. Kept as a kill-switch.
 *
 * Both sub-hooks are always invoked (gated by an `enabled` arg) so the
 * rules-of-hooks ordering never changes; the dispatcher returns the active
 * one. The flag is a build-time constant, so the branch is stable.
 */

import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { roleAtLeast } from '@lms/shared-types'
import type { Role } from '@lms/shared-types'
import { listMyLeaves } from '../api/me'
import { listEscalations } from '../api/escalations'
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications'
import { flags } from './feature-flags'
import {
  getReadSet,
  markAllReadInStorage,
  markReadInStorage,
  leaveToNotification,
  escalationToNotification,
  mergeNotifications,
} from './notifications'
import type { Notification, NotificationKind } from './notifications'

const POLL_INTERVAL_MS = 60_000
const BELL_PAGE_SIZE = 20

export interface UseNotificationsResult {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  markRead: (id: string) => void
  markAllRead: () => void
}

// ── Backend path (flag ON) ───────────────────────────────────────────────────

function useBackendNotifications(enabled: boolean): UseNotificationsResult {
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => listNotifications(BELL_PAGE_SIZE),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    retry: false,
  })

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => getUnreadCount(),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    retry: false,
  })

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] })
  }, [queryClient])

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidate,
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: invalidate,
  })

  const notifications: Notification[] = (listQuery.data ?? []).map((n) => {
    const base: Notification = {
      id: n.id,
      kind: n.kind as NotificationKind,
      message: n.message,
      occurredAt: n.createdAt,
      read: n.read,
    }
    // `to` is optional under exactOptionalPropertyTypes — omit the key
    // entirely rather than setting it to undefined.
    return n.link ? { ...base, to: n.link } : base
  })

  const markRead = useCallback(
    (id: string): void => markReadMutation.mutate(id),
    [markReadMutation],
  )
  const markAllRead = useCallback(
    (): void => markAllReadMutation.mutate(),
    [markAllReadMutation],
  )

  return {
    notifications,
    unreadCount: unreadQuery.data ?? 0,
    isLoading: listQuery.isLoading,
    markRead,
    markAllRead,
  }
}

// ── Legacy path (flag OFF) — client-side synthesis ───────────────────────────

function useLegacyNotifications(role: Role, enabled: boolean): UseNotificationsResult {
  const isDirectorOrAbove = roleAtLeast(role, 'director')
  const isHr = role === 'hr'

  const [readSet, setReadSet] = useState<Set<string>>(getReadSet)

  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key === 'lms.notifications.read') {
        setReadSet(getReadSet())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const [myLeavesQuery, escalationsQuery] = useQueries({
    queries: [
      {
        queryKey: ['notifications', 'my-leaves', role],
        queryFn: () => listMyLeaves(),
        enabled,
        refetchInterval: POLL_INTERVAL_MS,
        staleTime: POLL_INTERVAL_MS,
        retry: false,
      },
      {
        queryKey: ['notifications', 'escalations', role],
        queryFn: () => listEscalations(),
        enabled: enabled && (isDirectorOrAbove || isHr),
        refetchInterval: POLL_INTERVAL_MS,
        staleTime: POLL_INTERVAL_MS,
        retry: false,
      },
    ],
  })

  const myLeaveNotifications: Notification[] = (myLeavesQuery.data ?? []).flatMap((leave) => {
    const n = leaveToNotification(leave, readSet)
    return n ? [n] : []
  })

  const escalationNotifications: Notification[] = (escalationsQuery.data?.items ?? []).map(
    (row) => escalationToNotification(row, readSet),
  )

  const notifications = mergeNotifications([myLeaveNotifications, escalationNotifications])
  const unreadCount = notifications.filter((n) => !readSet.has(n.id)).length

  const markRead = useCallback((id: string): void => {
    markReadInStorage(id)
    setReadSet(getReadSet())
  }, [])

  const markAllRead = useCallback((): void => {
    const ids = notifications.map((n) => n.id)
    markAllReadInStorage(ids)
    setReadSet(getReadSet())
  }, [notifications])

  const isLoading =
    myLeavesQuery.isLoading || (escalationsQuery.isLoading && (isDirectorOrAbove || isHr))

  return { notifications, unreadCount, isLoading, markRead, markAllRead }
}

// ── Dispatcher ────────────────────────────────────────────────────────────────

export function useNotifications(role: Role): UseNotificationsResult {
  const useBackend = flags.notificationsBackend
  // Both hooks are always called (gated by `enabled`) to keep hook order
  // stable; the flag is a build-time constant so the returned branch never
  // flips between renders.
  const backend = useBackendNotifications(useBackend)
  const legacy = useLegacyNotifications(role, !useBackend)
  return useBackend ? backend : legacy
}
